"use client";

import StudyPlanSyncDialog from "@/components/StudyPlanSyncDialog";
import { getAllPlansDynamicState, syncStudyPlans } from "@/lib/server-actions/studyPlanActions";
import { useMemo, useState, useTransition } from "react";
import Link from "next/link";
import { studyTaskHref } from "@/lib/study-task-link";
import { CalendarCheck, Lock, Plus, Sprout } from "lucide-react";
import { toast } from "sonner";
import DashboardCard from "./DashboardCard";
import DashboardCardLoading from "./DashboardCardLoading";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import {
  usePlanDynamicState,
  studyStartedToast,
  subscribeAndRefetch,
  type SubscribeResult,
  type RunPendingWrite,
  type SetPlanDynamicState,
} from "@/hooks/usePlanDynamicState";
import { todayKstISO } from "@/lib/kst-date";
import {
  getStudyPlan,
  STUDY_PLAN_IDS,
  STUDY_PLAN_LABELS,
  type StudyPlanId,
} from "@/lib/study-plan";
import {
  applyToggleOverride,
  restoreTaskDynamicState,
  buildScheduledTasks,
  computeMetrics,
  tasksOnDate,
  personalStudyPlan,
  type OnTrackStatus,
  type PlanDynamicState,
  type ScheduledTask,
} from "@/lib/study-plan-state";
import { toggleTaskCompletion } from "@/lib/server-actions/studyPlanActions";
import { recoverStudyPlanRequest } from "@/lib/study-plan-request-error";
import { CODE_LANG_BADGE, codeLangLabel } from "../schedule/schedule-shared";

/**
 * 대시보드 "오늘의 학습" 위젯 (JCG-2310).
 *
 * 구독한 학습 계획(이론 4주 / 코딩 6주)에서 오늘 해야 할 task 만 추려 보여주고,
 * 체크박스로 바로 완료 처리(= 출석 적립)할 수 있게 한다. 매번 4week-study-plan 페이지로
 * 들어가지 않고 대시보드 최상단에서 오늘 일정에 접근·완료한다.
 *
 * 상태 분기:
 *   - 미로드: 스켈레톤
 *   - Starter 미만: 잠금 카드(/membership CTA)
 *   - 구독 0개: 시작 안내(이론/코딩 시작 버튼)
 *   - 구독 1+: 계획별 오늘 섹션 스택
 *
 * 색은 디자인 토큰 + amber 액센트(카드 기본) + 계획 라벨 칩만 계열색(이론=blue/코딩=green).
 * 정적 계획은 클라가 직접 import(study-plan-index.json), 동적 상태만 서버액션으로 받는다.
 *
 * 폼(JCG-2310): 대시보드 풀폭 "얇은 가로 바". 구독 계획마다 한 줄로 압축한다 —
 *   [좌] 메타 블록(plan 칩 · Day n/총 · x/y · 얇은 진행 바, shrink-0 인라인)
 *   [우] 칩 스트립(flex 단일 행, overflow-x-auto + 우측 페이드)
 * 행 안쪽 요소는 모두 h-7 이내로 맞춰 한 줄 높이를 최소화한다.
 */
export default function DashboardTodaySchedule({
  className,
}: {
  className?: string;
}) {
  const { isAdFree, isLoaded, isSignedIn } = useEffectivePass();
  // 동적 상태 1회 fetch(로그인 + Starter 이상에서만 의미가 있으나, 미구독 안내도 필요하므로
  // 로그인 시 항상 받아둔다. 게스트는 page.tsx가 GuestDashboardPreview로 처리).
  const {
    dynState,
    setDynState,
    runPendingWrite,
    loading: stateLoading,
    loadError,
    setLoadError,
    activeExamSessionId,
  } = usePlanDynamicState(
    !!(isLoaded && isSignedIn),
    "오늘의 학습 동적 상태 로드 오류:",
  );

  const [syncPrompt, setSyncPrompt] = useState<SubscribeResult | null>(null);
  const onSubscribed = (result: SubscribeResult) => { if (result.syncPlanIds?.length) setSyncPrompt(result); };

  // 1) 사용자 로드 전: 스켈레톤
  if (!isLoaded) {
    return (
      <DashboardCard icon={Sprout} title="오늘의 학습" className={className}>
        <DashboardCardLoading height={44} label="오늘의 학습 불러오는 중…" />
      </DashboardCard>
    );
  }

  // 2) 비로그인은 page.tsx에서 GuestDashboardPreview로 처리됨 → 안전망으로 렌더 생략
  if (!isSignedIn) return null;

  // 3) Starter 미만: 잠금 카드
  if (!isAdFree) {
    return (
      <DashboardCard icon={Sprout} title="오늘의 학습" className={className}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Lock className="h-4 w-4 shrink-0 text-amber-500" aria-hidden />
          <p className="text-sm text-muted-foreground">
            Starter 이용권으로 개인 학습 일정을 관리해 보세요.
          </p>
          <Link href="/membership" className="ml-auto shrink-0">
            <Button size="sm">Starter 이용권 보기</Button>
          </Link>
        </div>
      </DashboardCard>
    );
  }

  if (loadError) {
    return (
      <DashboardCard icon={Sprout} title="오늘의 학습" className={className}>
        <p role="alert" className="text-sm text-muted-foreground">
          {loadError}
        </p>
      </DashboardCard>
    );
  }

  // 4) 동적 상태 로딩 중
  if (stateLoading || !dynState || !activeExamSessionId) {
    return (
      <DashboardCard icon={Sprout} title="오늘의 학습" className={className}>
        <DashboardCardLoading height={44} label="오늘의 학습 불러오는 중…" />
      </DashboardCard>
    );
  }

  const subscribedIds = STUDY_PLAN_IDS.filter(
    (id) => dynState[id]?.subscribed && getStudyPlan(id, activeExamSessionId),
  );
  // 아직 시작 안 한 계획 — 구독 1+ 일 때도 나머지 계획을 시작할 수 있게 한다.
  const startableIds = STUDY_PLAN_IDS.filter(
    (id) => getStudyPlan(id, activeExamSessionId) && !dynState[id]?.subscribed,
  );

  // 5) 구독 0개: 시작 안내
  if (subscribedIds.length === 0) {
    return (
      <DashboardCard icon={Sprout} title="오늘의 학습" className={className}>
        <EmptyState
          setDynState={setDynState}
          runPendingWrite={runPendingWrite}
          setLoadError={setLoadError}
          activeExamSessionId={activeExamSessionId}
          onSubscribed={onSubscribed}
        />
      </DashboardCard>
    );
  }

  // 6) 구독 1+: 계획별 오늘 섹션 스택
  return (
    <DashboardCard
      icon={Sprout}
      title="오늘의 학습"
      href="/my/schedule"
      linkLabel="일정 관리"
      className={className}
    >
      {syncPrompt?.syncPlanIds && <StudyPlanSyncDialog
        open startedPlanId={syncPrompt.planId}
        planIds={subscribedIds} examSessionId={activeExamSessionId}
        onOpenChange={(open) => { if (!open) setSyncPrompt(null); }}
        onSync={(ids) => runPendingWrite(async () => {
          try {
            const result = await syncStudyPlans(ids, activeExamSessionId);
            if (!result.success) { toast.error(result.error); return false; }
            const next = await getAllPlansDynamicState(activeExamSessionId);
            setDynState(next.plans);
            toast.success(`${result.affectedCount ?? 0}개 항목의 완료 기록을 동기화했어요.`);
            return true;
          } catch (error) {
            if (!recoverStudyPlanRequest(error, setLoadError)) toast.error("동기화 결과를 확인하지 못했어요. 학습 일정에서 다시 확인해주세요.");
            return false;
          }
        })}
      />}
      <div className="space-y-2.5">
        {subscribedIds.map((id) => (
          <PlanTodaySection
            key={id}
            planId={id}
            examSessionId={activeExamSessionId}
            dyn={dynState[id]}
            runPendingWrite={runPendingWrite}
            setLoadError={setLoadError}
            onToggle={(task, done, completedAtISO) => {
              const before = dynState[id];
              setDynState((prev) =>
                applyToggleOverride(prev, task, done, completedAtISO),
              );
              return () => setDynState((prev) =>
                restoreTaskDynamicState(prev, task, before, true),
              );
            }}
          />
        ))}
        {startableIds.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-t border-border pt-2.5">
            <span className="shrink-0 text-[11px] text-muted-foreground">
              다른 계획도 시작할 수 있어요
            </span>
            <StartPlanButtons
              planIds={startableIds}
              setDynState={setDynState}
              runPendingWrite={runPendingWrite}
              setLoadError={setLoadError}
              examSessionId={activeExamSessionId}
              onSubscribed={onSubscribed}
            />
          </div>
        )}
      </div>
    </DashboardCard>
  );
}

// ── 미구독 안내 ──────────────────────────────────────────────────────────────

const PLAN_START_LABEL: Record<StudyPlanId, string> = {
  "theory-4week": "이론 4주 시작",
  "coding-6week": "코딩 6주 시작",
  "warrior-13day": "72시간의 전사 시작",
};

/** 시작 pill 색 — plan 칩과 동일 계열(위젯 칩들과 자연스럽게 어우러지게). */
const PLAN_START_PILL: Record<StudyPlanId, string> = {
  "theory-4week":
    "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/50 dark:text-blue-300 dark:hover:bg-blue-900/40",
  "coding-6week":
    "border-green-200 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-900 dark:bg-green-950/50 dark:text-green-300 dark:hover:bg-green-900/40",
  "warrior-13day": "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300 dark:hover:bg-amber-900/40",
};

function EmptyState({
  setDynState,
  runPendingWrite,
  setLoadError,
  activeExamSessionId,
  onSubscribed,
}: {
  setDynState: SetPlanDynamicState;
  runPendingWrite: RunPendingWrite;
  setLoadError: (message: string | null) => void;
  activeExamSessionId: string;
  onSubscribed: (result: SubscribeResult) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <span className="shrink-0 text-lg leading-none" aria-hidden>
        🥔
      </span>
      <p className="min-w-0 text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">
          아직 학습 계획을 시작하지 않았어요.
        </span>{" "}
        계획 페이지 일정 그대로 시작해 매일 할 일을 챙겨보세요.
      </p>
      <div className="ml-auto shrink-0">
        <StartPlanButtons
          planIds={STUDY_PLAN_IDS.filter((id) =>
            getStudyPlan(id, activeExamSessionId),
          )}
          setDynState={setDynState}
          runPendingWrite={runPendingWrite}
          setLoadError={setLoadError}
          examSessionId={activeExamSessionId}
          onSubscribed={onSubscribed}
        />
      </div>
    </div>
  );
}

/**
 * 미구독 계획 시작 버튼들. 구독 0개 안내(EmptyState)와 구독 1+ 일 때 "다른 계획도 시작" 행에서
 * 공용으로 쓴다 — 한 계획을 시작해도 나머지 계획을 계속 시작할 수 있다(이론↔코딩 독립 구독).
 */
function StartPlanButtons({
  planIds,
  setDynState,
  runPendingWrite,
  setLoadError,
  examSessionId,
  onSubscribed,
}: {
  planIds: StudyPlanId[];
  setDynState: SetPlanDynamicState;
  runPendingWrite: RunPendingWrite;
  setLoadError: (message: string | null) => void;
  examSessionId: string;
  onSubscribed: (result: SubscribeResult) => void;
}) {
  const [pending, startTransition] = useTransition();
  const [busyId, setBusyId] = useState<StudyPlanId | null>(null);

  const start = (planId: StudyPlanId) => {
    if (pending) return;
    setBusyId(planId);
    startTransition(async () => {
      // 구독 → 동적 상태 재조회(공용 subscribeAndRefetch). busy 상태만 자체 관리.
      const result = await runPendingWrite(() =>
        subscribeAndRefetch(
          setDynState,
          planId,
          examSessionId,
          setLoadError,
          todayKstISO(),
        ),
      );
      // 대시보드엔 다시 분배 버튼이 없으므로 학습 일정 화면의 버튼을 토스트로 안내한다(JCG-3652).
      if (result) {
        toast.success(...studyStartedToast(result, true));
        onSubscribed(result);
      }
      setBusyId(null);
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {planIds.map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => start(id)}
          disabled={pending}
          className={cn(
            "inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-60",
            PLAN_START_PILL[id],
          )}
        >
          <Plus className="h-3 w-3" aria-hidden />
          {busyId === id ? "시작하는 중…" : PLAN_START_LABEL[id]}
        </button>
      ))}
    </div>
  );
}

// ── 계획별 오늘 섹션 ──────────────────────────────────────────────────────────

/** 라벨 칩: 이론=blue, 코딩=green 톤 (dark 페어 포함). */
const PLAN_CHIP: Record<StudyPlanId, string> = {
  "theory-4week":
    "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",
  "coding-6week":
    "bg-green-50 text-green-700 dark:bg-green-950/50 dark:text-green-300",
  "warrior-13day": "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
};

/** 진행 하프라인(헤더 분수 옆 얇은 바) plan 계열 채움색. */
const PLAN_BAR: Record<StudyPlanId, string> = {
  "theory-4week": "bg-blue-500 dark:bg-blue-400",
  "coding-6week": "bg-green-500 dark:bg-green-400",
  "warrior-13day": "bg-amber-500 dark:bg-amber-400",
};

function PlanTodaySection({
  planId,
  examSessionId,
  dyn,
  onToggle,
  runPendingWrite,
  setLoadError,
}: {
  planId: StudyPlanId;
  examSessionId: string;
  dyn: PlanDynamicState;
  onToggle: (task: ScheduledTask, done: boolean, completedAtISO: string) => () => void;
  runPendingWrite: RunPendingWrite;
  setLoadError: (message: string) => void;
}) {
  const currentPlan = getStudyPlan(planId, examSessionId);
  const plan = currentPlan ? personalStudyPlan(currentPlan, dyn) : null;
  const todayISO = todayKstISO();

  const { todayTasks, metrics } = useMemo(() => {
    if (!plan) {
      return { todayTasks: [], metrics: null };
    }
    const sched = buildScheduledTasks(plan, dyn);
    const today = tasksOnDate(sched, todayISO).filter((t) => !t.skipped);
    const m = computeMetrics(plan, sched, dyn, todayISO);
    return { todayTasks: today, metrics: m };
  }, [plan, dyn, todayISO]);

  if (!plan || !metrics) return null;

  const doneCount = todayTasks.filter((t) => t.done).length;
  const totalCount = todayTasks.length;
  const percent =
    totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
  const examReached =
    metrics.todayDayNumber != null && metrics.todayDayNumber >= plan.totalDays;
  const allDone = totalCount > 0 && doneCount === totalCount;

  // 칩 스트립은 계획 원본 순서 그대로(재정렬 X) — 완료가 앞이어도 일정 순서를 유지한다.
  //
  // 얇은 풀폭 행: 좌측 메타 블록(shrink-0) + 우측 칩 스트립(flex-1 min-w-0, 단일 행).
  // 좁은 화면에서는 메타가 위, 칩 스트립이 아래로 자연 줄바꿈(flex-wrap).
  return (
    <section className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      {/* 좌측 메타 블록: 계획 칩 · Day n/총 · done/total + 얇은 진행 바 (한 줄, 인라인) */}
      <div className="flex h-7 shrink-0 items-center gap-x-2">
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-[11px] font-bold",
            PLAN_CHIP[planId],
          )}
        >
          {STUDY_PLAN_LABELS[planId]}
        </span>
        {metrics.todayDayNumber != null && (
          <span className="text-[11px] font-semibold tabular-nums text-muted-foreground">
            Day {Math.min(metrics.todayDayNumber, plan.totalDays)}/
            {plan.totalDays}
          </span>
        )}
        {totalCount > 0 && (
          <>
            <span
              className={cn(
                "text-[11px] font-bold tabular-nums",
                allDone
                  ? "text-green-600 dark:text-green-400"
                  : "text-amber-600 dark:text-amber-400",
              )}
            >
              {doneCount}/{totalCount}
            </span>
            {/* 얇은 진행 바: 시각 보조용(스크린리더는 분수 텍스트로 충분, aria-hidden). */}
            <span
              className="h-1 w-10 overflow-hidden rounded-full bg-muted"
              aria-hidden
            >
              <span
                className={cn(
                  "block h-full rounded-full transition-[width] duration-300 ease-out motion-reduce:transition-none",
                  allDone ? "bg-green-500 dark:bg-green-400" : PLAN_BAR[planId],
                )}
                style={{ width: `${percent}%` }}
              />
            </span>
          </>
        )}
      </div>

      {/* 우측 칩 스트립(또는 쉬는 날 메시지): 남는 폭을 차지하고 한 줄 가로 스크롤 */}
      <div className="min-w-0 flex-1 basis-48">
        {totalCount === 0 ? (
          <RestDayMessage examReached={examReached} status={metrics.status} />
        ) : (
          <TaskChipStrip
            tasks={todayTasks}
            planId={planId}
            examSessionId={examSessionId}
            onToggle={onToggle}
            runPendingWrite={runPendingWrite}
            setLoadError={setLoadError}
            allDone={allDone}
          />
        )}
      </div>
    </section>
  );
}

/** 오늘 task 없음(쉬는 날 / 시험 도달). 한 줄 얇은 인라인 메시지(h-7). */
function RestDayMessage({
  examReached,
  status,
}: {
  examReached: boolean;
  status: OnTrackStatus;
}) {
  if (examReached || status === "done") {
    return (
      <div className="flex h-7 items-center gap-1.5 rounded-full bg-amber-50 px-3 dark:bg-amber-950/40">
        <CalendarCheck
          className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden
        />
        <p className="truncate text-xs font-medium text-amber-700 dark:text-amber-300">
          여기까지 온 당신, 정말 대단해요. 시험장에서 만나요! 🥔
        </p>
      </div>
    );
  }
  return (
    <div className="flex h-7 items-center gap-1.5 rounded-full bg-muted/60 px-3">
      <span className="text-sm leading-none" aria-hidden>
        😌
      </span>
      <p className="truncate text-xs text-muted-foreground">
        오늘은 쉬어가는 날이에요. 푹 쉬고 내일 또 만나요!
      </p>
    </div>
  );
}

// ── 가로 칩(pill) 스트립 ──────────────────────────────────────────────────────

/** 계획별 미완료 pill 좌측 점(이론=blue / 코딩=green, dark 페어 포함). */
const PLAN_DOT: Record<StudyPlanId, string> = {
  "theory-4week": "bg-blue-400 dark:bg-blue-500",
  "coding-6week": "bg-green-400 dark:bg-green-500",
  "warrior-13day": "bg-amber-400 dark:bg-amber-500",
};

/**
 * 오늘 task 를 칩(pill) 스트립으로 나열(단일 행).
 *
 * - 계획 원본 순서 그대로(재정렬 X). 풀폭이라 대부분 하루치가 한 줄에 들어가고,
 *   넘치면 가로 스크롤(overflow-x-auto) + 우측 페이드로 스크롤 가능함을 암시.
 * - 전부 완료면 작은 완료 배지를 끝에 덧붙인다(칩은 ✓ 상태 그대로 유지).
 */
function TaskChipStrip({
  tasks,
  planId,
  examSessionId,
  onToggle,
  runPendingWrite,
  setLoadError,
  allDone,
}: {
  tasks: ScheduledTask[];
  planId: StudyPlanId;
  examSessionId: string;
  onToggle: (task: ScheduledTask, done: boolean, completedAtISO: string) => () => void;
  runPendingWrite: RunPendingWrite;
  setLoadError: (message: string) => void;
  allDone: boolean;
}) {
  return (
    <div className="relative">
      <ul
        className="flex items-center gap-1.5 overflow-x-auto pr-6 [scrollbar-width:thin]"
        aria-label="오늘 할 일 목록"
      >
        {tasks.map((task) => (
          <TaskChip
            key={task.uid}
            task={task}
            planId={planId}
            examSessionId={examSessionId}
            onToggle={onToggle}
            runPendingWrite={runPendingWrite}
            setLoadError={setLoadError}
          />
        ))}
        {allDone && (
          <li className="shrink-0">
            <span className="flex h-7 items-center gap-1 rounded-full bg-green-50 px-2.5 text-[11px] font-bold text-green-700 dark:bg-green-950/50 dark:text-green-300">
              오늘 학습 완료! 🎉
            </span>
          </li>
        )}
      </ul>
      <span
        className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-card to-transparent"
        aria-hidden
      />
    </div>
  );
}

/**
 * 단일 task pill.
 *
 * 역할 분리(a11y):
 *  - 체크 영역 = `<button role="checkbox">`(완료 토글). aria-checked + aria-label.
 *  - 제목 영역 = `<Link>`(task.path 이동). 이모지+텍스트.
 * 둘은 형제 인터랙티브 요소로 한 pill 안에 나란히 둔다(스트레치 링크 X — 역할 충돌 방지).
 *
 * 시각:
 *  - 미완료: 외곽선 pill(`border`) + 좌측 plan 색 점 + ○ 체크.
 *  - 완료: 채움/톤다운 pill(amber 틴트) + ✓ 체크 + 제목 취소선.
 * 제목은 한 줄 유지(whitespace-nowrap) — 길면 칩이 늘어나고 스트립이 스크롤된다.
 */
function TaskChip({
  task,
  planId,
  examSessionId,
  onToggle,
  runPendingWrite,
  setLoadError,
}: {
  task: ScheduledTask;
  planId: StudyPlanId;
  examSessionId: string;
  onToggle: (task: ScheduledTask, done: boolean, completedAtISO: string) => () => void;
  runPendingWrite: RunPendingWrite;
  setLoadError: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  const handleToggle = async () => {
    if (busy) return; // 중복 클릭 방지
    const next = !task.done;
    // 실패하면 역토글로 현재 시각을 쓰지 않고 요청 전 완료 기록을 복원한다.
    const completedAtISO = new Date().toISOString();
    setBusy(true);
    await runPendingWrite(async () => {
      const rollback = onToggle(task, next, completedAtISO);
      try {
        const res = await toggleTaskCompletion(
          task.planId,
          task.originDayNumber,
          task.path,
          task.storageCodeLang ?? task.codeLang,
          examSessionId,
        );
        if (!res.success) {
          rollback();
          if (res.needsLogin) toast.error("로그인이 필요해요.");
          else toast.error(res.error ?? "완료 처리에 실패했어요.");
        } else if (typeof res.done === "boolean" && res.done !== next) {
          // 서버 판정이 예상과 다르면 서버 값으로 보정
          onToggle(task, res.done, completedAtISO);
        }
      } catch (error) {
        rollback();
        if (recoverStudyPlanRequest(error, setLoadError)) return;
        toast.error("완료 처리에 실패했어요.");
      } finally {
        setBusy(false);
      }
    });
  };

  // 다국어 코딩 task(같은 제목 C/Java/Python 3개)를 pill 에서 언어 뱃지로 구분(JCG-2658).
  // 빈 값이면 뱃지 없이 기존과 동일. 스크린리더는 aria-label 에 언어를 덧붙여 구분.
  const langLabel = codeLangLabel(task.codeLang);
  const titleWithLang = langLabel ? `${task.title} ${langLabel}` : task.title;

  return (
    <li className="shrink-0 snap-start">
      <div
        className={cn(
          "flex h-7 items-center gap-1 rounded-full border pl-1 pr-2.5 transition-colors",
          task.done
            ? "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50"
            : "border-border bg-background hover:border-amber-300 dark:hover:border-amber-800",
        )}
      >
        {/* 체크 영역(완료 토글). 24px+ 탭 타깃 + before:-inset 로 ~40px 확보. */}
        <button
          type="button"
          role="checkbox"
          aria-checked={task.done}
          aria-label={`${titleWithLang} 완료 표시`}
          disabled={busy}
          onClick={handleToggle}
          className={cn(
            "relative flex h-5 w-5 shrink-0 touch-manipulation items-center justify-center rounded-full border transition-colors before:absolute before:-inset-1.5 before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:opacity-60",
            task.done
              ? "border-amber-500 bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950"
              : "border-amber-400 bg-background text-transparent",
          )}
        >
          {task.done ? (
            <svg
              viewBox="0 0 14 14"
              className="h-3 w-3"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M3 7.5 6 10.5 11 4" />
            </svg>
          ) : (
            // 미완료: plan 계열 점으로 채워 "할 일"임을 은은히 표시.
            <span
              className={cn("h-1.5 w-1.5 rounded-full", PLAN_DOT[planId])}
              aria-hidden
            />
          )}
        </button>

        {/* 제목 영역(이동 링크). 이모지 + 제목 한 줄. */}
        <Link
          href={studyTaskHref(task)}
          className={cn(
            "flex items-center gap-1 whitespace-nowrap rounded text-[12px] leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
            task.done
              ? "text-muted-foreground line-through decoration-amber-500/50"
              : "font-medium text-foreground hover:text-amber-600 dark:hover:text-amber-400",
          )}
        >
          {task.emoji && (
            <span className="text-[13px] leading-none" aria-hidden>
              {task.emoji}
            </span>
          )}
          {/* 언어 뱃지(중립 톤). 정보는 aria-label(titleWithLang)이 보존하므로 aria-hidden. */}
          {langLabel && (
            <span className={CODE_LANG_BADGE} aria-hidden>
              {langLabel}
            </span>
          )}
          {task.title}
        </Link>
      </div>
    </li>
  );
}
